import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useNavigate } from 'react-router'
import { cn } from '~/shared/lib/cn'
import { Imagery } from '~/shared/ui/imagery'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/shared/ui/table'
import { Text } from '~/shared/ui/text'
import { Tooltip } from '../local-tooltip'

interface DataTableProps<TData> {
  // Using any here because the values can be of any type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[]
  data: TData[]
  containerClassName?: string
  withStickyHeader?: boolean
  footer?: React.ReactNode
  getRowUrl?: (row: TData) => string
  noResultsMessage?: string
  noResultsAction?: React.ReactNode
  disabledRowTip?: (row: TData) => string | null
}

export function DataTable<TData>({
  columns,
  data,
  containerClassName,
  withStickyHeader,
  footer,
  getRowUrl,
  noResultsMessage,
  noResultsAction,
  disabledRowTip,
}: DataTableProps<TData>) {
  const navigate = useNavigate()

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>, url: string) => {
    if (url && (e.metaKey || e.ctrlKey || e.button === 1)) {
      window.open(url, '_blank')
    } else {
      navigate(url)
    }
  }

  return (
    <>
      <Table
        containerClassName={containerClassName}
        footer={footer}
      >
        <TableHeader
          className={cn({
            'sticky top-0 z-1': withStickyHeader,
          })}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        {table.getRowModel().rows?.length ? (
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              const tooltipContent = disabledRowTip?.(row.original)
              const isDisabled = !!tooltipContent
              return (
                <Tooltip
                  key={row.id}
                  content={tooltipContent}
                  open={!isDisabled ? false : undefined}
                >
                  <TableRow
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={(e) => {
                      if (!isDisabled && getRowUrl) {
                        handleRowClick(e, getRowUrl(row.original))
                      }
                    }}
                    disabled={isDisabled}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                </Tooltip>
              )
            })}
          </TableBody>
        ) : (
          <tbody>
            <TableRow>
              <TableCell
                className="h-24 border-none text-center"
                colSpan={columns.length}
              >
                <div className="flex flex-col items-center p-4">
                  <Imagery
                    className="text-content-medium-emphasis"
                    name="searchResults"
                  />
                  <Text
                    className="mt-4"
                    appearance="SUBTITLE"
                  >
                    Oops, Empty for Now
                  </Text>
                  {noResultsMessage ? (
                    <Text
                      className="text-text-tertiary mt-2"
                      appearance="BODY2"
                    >
                      {noResultsMessage}
                    </Text>
                  ) : null}
                  {noResultsAction ? <div className="mt-6">{noResultsAction}</div> : null}
                </div>
              </TableCell>
            </TableRow>
          </tbody>
        )}
      </Table>
    </>
  )
}
